import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import QuantityUnitCard from "../components/quantityUnits/QuantityUnitCard";
import CreateQuantityUnitModal from "../components/quantityUnits/CreateQuantityUnitModal";
import UpdateQuantityUnitModal from "../components/quantityUnits/UpdateQuantityUnitModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getQuantityUnits,
  deleteQuantityUnit,
  resetQuantityUnitServerStatus,
} from "../redux/actions/quantityUnitActions";
import PageError from "../components/common/PageError";
import { QuantityUnitResponseDto } from "../shared/dtos/quantityUnit/QuantityUnitResponseDto";

const QuantityUnitsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // Redux State
  const {
    items: quantityUnits,
    pagination,
    loadingFetchAll: loadingQuantityUnits,
    errorFetchAll: errorLoadingQuantityUnits,
    serverStatus,
    loadingDelete: loadingQuantityUnitDelete,
    loadingUpdate: loadingQuantityUnitUpdate,
    loadingCreate: loadingQuantityUnitCreate,
    errorCreate: errorCreatingQuantityUnit,
    errorUpdate: errorUpdatingQuantityUnit,
  } = useAppSelector((state) => state.quantityUnit);

  // Local State
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(12);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] =
    useState<QuantityUnitResponseDto | null>(null);

  // Permissions
  const canCreate = hasPermission("quantity_unit:create");
  const canUpdate = hasPermission("quantity_unit:update");
  const canDelete = hasPermission("quantity_unit:delete");

  // Reset page when searching
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Fetch Data
  const fetchUnits = useCallback(() => {
    dispatch(getQuantityUnits({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  // Handlers
  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ): void => setPage(value);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(
    () => !loadingQuantityUnitCreate && setIsCreateModalOpen(false),
    [loadingQuantityUnitCreate],
  );

  const handleOpenUpdate = (unit: QuantityUnitResponseDto): void => {
    setSelectedUnit(unit);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingQuantityUnitUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedUnit(null);
    }
  }, [loadingQuantityUnitUpdate]);

  const handleOpenDelete = (unit: QuantityUnitResponseDto): void => {
    setSelectedUnit(unit);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingQuantityUnitDelete) {
      setIsDeleteModalOpen(false);
      setSelectedUnit(null);
    }
  }, [loadingQuantityUnitDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedUnit) dispatch(deleteQuantityUnit(selectedUnit.id));
  };

  // Auto Close on Success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(resetQuantityUnitServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingQuantityUnits) return <AppCircularProgress />;

    if (errorLoadingQuantityUnits)
      return (
        <PageError error={errorLoadingQuantityUnits} onRetry={fetchUnits} />
      );

    if (!quantityUnits || quantityUnits.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No units found matching your criteria.</Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {quantityUnits.map((unit) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={unit.id}>
              <QuantityUnitCard
                unit={unit}
                onEdit={canUpdate ? () => handleOpenUpdate(unit) : undefined}
                onDelete={canDelete ? () => handleOpenDelete(unit) : undefined}
              />
            </Grid>
          ))}
        </Grid>

        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Quantity Units"
      subtitle="Define measurement units (e.g., KG, Litres, Pcs) for products and inventory."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Unit
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search units by name or code..."
      />

      {renderContent()}

      {isCreateModalOpen && (
        <CreateQuantityUnitModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingQuantityUnitCreate}
          error={errorCreatingQuantityUnit}
        />
      )}

      {isUpdateModalOpen && selectedUnit && (
        <UpdateQuantityUnitModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          unit={selectedUnit}
          loading={loadingQuantityUnitUpdate}
          error={errorUpdatingQuantityUnit}
        />
      )}

      {isDeleteModalOpen && selectedUnit && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Quantity Unit"
          description={`Are you sure you want to delete "${selectedUnit.name} (${selectedUnit.code})"? This may affect products using this unit.`}
          loading={loadingQuantityUnitDelete}
          severity="error"
        />
      )}
    </PageContainer>
  );
};

export default QuantityUnitsPage;
